import { Badge, Tooltip } from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { djs } from '@duke-hq/djs';

/**
 * Shared publish-status badge. Editors without a version number (e.g. courses) pass only
 * `publishedAt` and get a date-only tooltip.
 *
 * When the draft has moved ahead (`hasUnpublishedChanges`), the Published pill is replaced
 * by a neutral "Unpublished changes" pill: "Published" is misleading (the version being
 * edited isn't itself published), and flipping to "Draft" would wrongly imply nothing is
 * published. Grey, not amber, since unpublished edits are the normal state of editing and
 * amber is claimed by the "Load latest" button.
 */
export function PublishedStatusBadge({
  isPublished,
  publishedVersion,
  publishedAt,
  hasUnpublishedChanges = false,
}: {
  isPublished: boolean;
  publishedVersion?: number | null;
  publishedAt?: Date | string | null;
  hasUnpublishedChanges?: boolean;
}) {
  const showUnpublishedChanges = isPublished && hasUnpublishedChanges;

  if (showUnpublishedChanges) {
    // Name the version publishing will create (the next one), since that's what the author
    // is about to make; past versions live in the adjacent version history.
    return (
      <Tooltip
        label={
          publishedVersion != null
            ? t`Publish to include your latest changes in version ${publishedVersion + 1}.`
            : t`Publish to include your latest changes.`
        }
        placement="bottom"
      >
        <Badge
          variant="pill"
          colorScheme="gray"
          fontSize="xs"
          fontWeight="semibold"
          cursor="default"
        >
          <Trans>Unpublished changes</Trans>
        </Badge>
      </Tooltip>
    );
  }

  const statusBadge = (
    <Badge
      colorScheme={isPublished ? 'brand' : 'gray'}
      variant="pill"
      fontSize="xs"
      fontWeight="semibold"
      cursor="default"
    >
      {isPublished ? <Trans>Published</Trans> : <Trans>Draft</Trans>}
    </Badge>
  );

  if (!isPublished || !publishedAt) {
    return statusBadge;
  }

  return (
    <Tooltip
      label={
        publishedVersion != null
          ? t`Version ${publishedVersion} · ${date(publishedAt)}`
          : t`Published ${date(publishedAt)}`
      }
      // Below the badge (over the canvas), not the default above amid the toolbar.
      placement="bottom"
    >
      {statusBadge}
    </Tooltip>
  );
}

function date(value: Date | string) {
  return djs(value).format('D MMM YYYY, h:mma');
}
